import { X, RefreshCw, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { metricDefs } from '../metric-defs';
import { SCALAR_METRIC_KEYS } from '@/types/analyticsV2';
import type { SelectedAccountRef } from '../system-admin-ui-config';
import type { AccountRowState } from './use-consolidated-metrics';
import { MetricStat } from './metric-stat';
import { accountInitials, avatarColor } from './metric-accents';

interface AccountMetricsRowProps {
  account: SelectedAccountRef;
  state: AccountRowState | undefined;
  onOpen: (account: SelectedAccountRef) => void;
  onRemove: (id: string) => void;
  onRetry: (accountId: number) => void;
}

export function AccountMetricsRow({ account, state, onOpen, onRemove, onRetry }: AccountMetricsRowProps) {
  const status = state?.status ?? 'loading';
  const isLoading = status === 'loading';
  const isError = status === 'error';

  return (
    <div
      className='group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-gradient-to-r hover:from-violet-50/70 hover:to-transparent'
      onClick={() => onOpen(account)}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(account);
        }
      }}
    >
      <div className='flex w-48 shrink-0 items-center gap-2.5'>
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold',
            avatarColor(account.id),
          )}
        >
          {accountInitials(account.name, account.id)}
        </div>
        <div className='min-w-0'>
          <div className='truncate text-[13px] font-semibold text-gray-900'>
            {account.name ?? `Account #${account.id}`}
          </div>
          <div className='text-[11px] font-medium text-gray-500'>ID {account.id}</div>
        </div>
      </div>

      {isError ? (
        <div className='flex flex-1 items-center gap-2 text-[12px] text-rose-500'>
          Could not load this account.
          <Button
            variant='ghost'
            size='sm'
            className='h-6 gap-1 px-2 text-[11px]'
            onClick={(e) => {
              e.stopPropagation();
              onRetry(Number(account.id));
            }}
          >
            <RefreshCw className='h-3 w-3' /> Retry
          </Button>
        </div>
      ) : (
        <div className='grid min-w-0 flex-1 grid-cols-3 gap-x-4 gap-y-2 xl:grid-cols-6'>
          {SCALAR_METRIC_KEYS.map((key) => (
            <MetricStat key={key} def={metricDefs[key]} result={state?.data?.[key]} isLoading={isLoading} />
          ))}
        </div>
      )}

      <div className='flex shrink-0 items-center gap-1'>
        <button
          type='button'
          className='flex h-7 w-7 items-center justify-center rounded-full text-gray-400 opacity-0 transition hover:bg-rose-50 hover:text-rose-500 focus-visible:opacity-100 group-hover:opacity-100'
          onClick={(e) => {
            e.stopPropagation();
            onRemove(account.id);
          }}
          aria-label={`Remove account ${account.id}`}
        >
          <X className='h-3.5 w-3.5' />
        </button>
        <ChevronRight className='h-4 w-4 text-gray-300 transition-colors group-hover:text-violet-500' />
      </div>
    </div>
  );
}
